import moment from "moment-timezone";
import { IAvailableSlot, IReservedSlot } from "./helper";

// The server stores availability and interviews in UTC: a slot is "MM-DD" plus an
// hour, both UTC. People should see and pick times in their own time zone, so the
// calendar converts at the edge - UTC in from the API, local for the grid, UTC
// again on the way back out. Nothing behind the API changes shape.

const browserZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

// The grid has one row per whole hour, and hours are stored as whole UTC hours, so
// a zone whose offset is not a whole number of hours (India, Nepal, ...) cannot be
// mapped onto it without moving every slot by 30 minutes. Those users are shown UTC
// - said plainly on screen - rather than silently shifted times.
const isWholeHourZone = (zone: string) =>
  [moment.tz([2026, 0, 1], zone), moment.tz([2026, 6, 1], zone)].every((m) => m.utcOffset() % 60 === 0);

export const zoneIsSupported = () => isWholeHourZone(browserZone());
export const activeZone = (): string => (zoneIsSupported() ? browserZone() : "UTC");

export const zoneLabel = (): string => {
  const zone = activeZone();
  const now = moment.tz(zone);
  return `${zone.replace(/_/g, " ")} (${now.format("z")}, UTC${now.format("Z")})`;
};

// "PKT", "PST", or "UTC+5" where the zone has no abbreviation.
export const zoneAbbreviation = (): string => {
  const abbr = moment.tz(activeZone()).format("z");
  return /^[+-]\d+$/.test(abbr) ? `UTC${abbr}` : abbr;
};

export interface ZonedSlot {
  date: string; // MM-DD
  hour: number;
  year: number;
}

export const localToUtc = (date: string, hour: number, year: number): ZonedSlot => {
  const [month, day] = date.split("-").map(Number);
  const utc = moment.tz([year, month - 1, day, hour], activeZone()).utc();
  return { date: utc.format("MM-DD"), hour: utc.hour(), year: utc.year() };
};

const utcToLocal = (date: string, hour: number, year: number) => {
  const [month, day] = date.split("-").map(Number);
  const local = moment.utc([year, month - 1, day, hour]).tz(activeZone());
  return { date: local.format("MM-DD"), hour: local.hour(), month0: local.month(), year: local.year() };
};

// The UTC months (1-12) whose data can fall inside a local month: its first and
// last hour are in different UTC months for anyone away from UTC.
export const utcMonthsFor = (month0: number, year: number): { month: number; year: number }[] => {
  const zone = activeZone();
  const start = moment.tz([year, month0, 1], zone).utc();
  const end = moment.tz([year, month0 + 1, 1], zone).utc().subtract(1, "hour");
  const seen = new Map<string, { month: number; year: number }>();
  [start, end].forEach((m) => seen.set(`${m.year()}-${m.month()}`, { month: m.month() + 1, year: m.year() }));
  return Array.from(seen.values());
};

interface Chunk<T> {
  items: T[];
  utcYear: number;
}

// Slots grouped by day. `year` is kept when given (stored slots always have one);
// the local grid works within a single month and leaves it off.
const group = (flat: { date: string; hour: number; year?: number }[]): IAvailableSlot[] => {
  const byDay = new Map<string, { date: string; year?: number; hours: Set<number> }>();
  flat.forEach(({ date, hour, year }) => {
    const k = `${year ?? ""}|${date}`;
    const day = byDay.get(k) || { date, year, hours: new Set<number>() };
    byDay.set(k, { ...day, hours: day.hours.add(hour) });
  });
  return Array.from(byDay.values()).map(({ date, year, hours }) => ({
    date,
    ...(year !== undefined && { year }),
    time: Array.from(hours).sort((a, b) => a - b),
  }));
};

// UTC slots -> local slots, keeping only those that fall in the given local month.
export const localizeSlots = (chunks: Chunk<IAvailableSlot>[], month0: number, year: number): IAvailableSlot[] =>
  group(
    chunks.flatMap(({ items, utcYear }) =>
      items.flatMap((slot) =>
        (slot.time || [])
          .map((h) => utcToLocal(slot.date, h, slot.year ?? utcYear))
          .filter((l) => l.month0 === month0 && l.year === year)
          .map((l) => ({ date: l.date, hour: l.hour }))
      )
    )
  );

export const localizeReserved = (chunks: Chunk<IReservedSlot>[], month0: number, year: number): IReservedSlot[] =>
  chunks.flatMap(({ items, utcYear }) =>
    items.flatMap((slot) => {
      const l = utcToLocal(slot.date, +slot.time, slot.year ?? utcYear);
      return l.month0 === month0 && l.year === year ? [{ ...slot, date: l.date, time: l.hour }] : [];
    })
  );

// One month of someone's calendar, in local time. `fetchUtc` loads a UTC month;
// it is called for every UTC month the local month touches.
export const loadLocalMonth = async (
  month0: number,
  year: number,
  fetchUtc: (utcMonth: number, utcYear: number) => Promise<{ availableSlots?: IAvailableSlot[]; reservedSlots?: IReservedSlot[] }>
) => {
  const parts = await Promise.all(
    utcMonthsFor(month0, year).map(async ({ month, year: utcYear }) => ({ utcYear, data: (await fetchUtc(month, utcYear)) || {} }))
  );
  return {
    availableSlots: localizeSlots(parts.map((p) => ({ items: p.data.availableSlots || [], utcYear: p.utcYear })), month0, year),
    reservedSlots: localizeReserved(parts.map((p) => ({ items: p.data.reservedSlots || [], utcYear: p.utcYear })), month0, year),
  };
};

// Local slots for a month -> the UTC slots to store, plus the exact span of UTC
// time being replaced so the server does not touch hours outside this local month.
export const delocalizeSlots = (slots: IAvailableSlot[], month0: number, year: number) => {
  const zone = activeZone();
  return {
    availableSlots: group(
      slots.flatMap((slot) =>
        (slot.time || []).map((h) => {
          const u = localToUtc(slot.date, h, year);
          return { date: u.date, hour: u.hour, year: u.year };
        })
      )
    ),
    replaceFrom: moment.tz([year, month0, 1], zone).utc().toISOString(),
    replaceTo: moment.tz([year, month0 + 1, 1], zone).utc().toISOString(),
  };
};

// A single stored UTC slot shown as a local date and hour.
export const utcSlotToLocalDisplay = (date: string, hour: number, year?: number): { date: string; hour: number; when: moment.Moment } => {
  const now = moment.utc();
  const [month, day] = date.split("-").map(Number);
  let when = moment.utc([year ?? now.year(), month - 1, day, hour]);
  // Without a year, the slot is its next occurrence - suggestion windows only look ahead.
  if (year === undefined && when.isBefore(now.clone().subtract(1, "day"))) when = when.add(1, "year");
  const local = when.clone().tz(activeZone());
  return { date: local.format("MM-DD"), hour: local.hour(), when: local };
};
